import React from 'react';
import Footer from './Footer';

interface AboutPageProps {
  darkMode: boolean;
}

const AboutPage: React.FC<AboutPageProps> = ({ darkMode }) => {
  const theme = {
    bg: darkMode ? 'bg-[#1e1e1e]' : 'bg-gray-100',
    panelBg: darkMode ? 'bg-[#252526]' : 'bg-white',
    text: darkMode ? 'text-[#d4d4d4]' : 'text-gray-900',
    mutedText: darkMode ? 'text-[#858585]' : 'text-gray-500',
    label: darkMode ? 'text-[#bbbbbb]' : 'text-gray-700',
    border: darkMode ? 'border-[#3e3e42]' : 'border-gray-200',
    link: darkMode ? 'text-[#4a9eff] hover:text-[#6bb5ff]' : 'text-blue-600 hover:text-blue-700',
  };

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.text} py-12 px-4`}>
      <div className={`max-w-4xl mx-auto ${theme.panelBg} rounded-lg shadow-lg p-8 ${theme.border} border`}>
        <h1 className={`text-3xl font-bold mb-6 ${theme.text}`}>About JSON Formatter</h1>

        <div className="prose prose-invert max-w-none space-y-6">
          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>What is JSON Formatter?</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              JSON Formatter is a free, professional online tool designed to help developers work with JSON data efficiently. 
              Whether you're debugging API responses, formatting configuration files, or validating data structures, our tool 
              provides a fast, secure, and user-friendly experience.
            </p>
            <p className={`mb-4 ${theme.text} leading-7`}>
              Unlike many online JSON tools, our formatter processes all data <strong className="font-semibold">entirely in your browser</strong>, 
              ensuring that your sensitive information never leaves your device. This makes it safe for working with API keys, 
              user data, and internal configurations.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>Key Features</h2>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li><strong>Format & Beautify:</strong> Convert messy JSON into clean, readable, indented code</li>
              <li><strong>Validate Syntax:</strong> Instantly detect and highlight JSON errors with clear error messages</li>
              <li><strong>Minify & Compress:</strong> Remove whitespace to reduce file size for production use</li>
              <li><strong>Interactive Tree View:</strong> Navigate complex JSON structures with expandable/collapsible nodes</li>
              <li><strong>File Support:</strong> Upload <code className="px-1.5 py-0.5 bg-gray-700 rounded">.json</code> or <code className="px-1.5 py-0.5 bg-gray-700 rounded">.txt</code> files and download results</li>
              <li><strong>Search & Replace:</strong> Find and replace text within your JSON (Ctrl+F)</li>
              <li><strong>Dark Mode:</strong> Comfortable viewing in both light and dark themes</li>
              <li><strong>Auto-Save:</strong> Your work is automatically saved to browser storage</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>Privacy & Security</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              Privacy is at the core of our design. All JSON processing happens locally in your browser using native JavaScript 
              APIs. We don't send your data to any server, store it, or log it. This means:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li>Your JSON data stays completely private</li>
              <li>Safe for sensitive information like API keys and user data</li>
              <li>No account registration required</li>
              <li>Works entirely offline after initial page load</li>
            </ul>
            <p className={`mb-4 ${theme.text} leading-7`}>
              For more details, please see our{' '}
              <a href="/privacy" className={`${theme.link} underline`}>Privacy Policy</a>.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>Part of EchoLog Ecosystem</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              JSON Formatter is part of the <strong className="font-semibold">EchoLog</strong> ecosystem, a collection of tools 
              focused on accessibility and developer productivity. EchoLog's mission is to build accessible, privacy-conscious tools 
              that make both accessibility and developer workflows better.
            </p>
            <p className={`mb-4 ${theme.text} leading-7`}>
              Other tools in our ecosystem include:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li>
                <a href="https://echologai.com" target="_blank" rel="noopener noreferrer" className={`${theme.link} underline`}>
                  EchoLog AI Voice Assistant
                </a> - Chrome extension for hands-free computer navigation
              </li>
              <li>
                <a href="https://protobuf.echologai.com" target="_blank" rel="noopener noreferrer" className={`${theme.link} underline`}>
                  Proto Workbench
                </a> - Online Protobuf compiler and decoder
              </li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>Who We Are</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              EchoLog is built by a small, focused team that cares deeply about accessibility, privacy, and developer experience. 
              We combine experience across software engineering, AI, and user experience design to create products that are both 
              powerful and approachable.
            </p>
            <p className={`mb-4 ${theme.text} leading-7`}>
              Our principles:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-2 ${theme.text} leading-7`}>
              <li><strong>Privacy by Design:</strong> We minimize data collection and keep computation on the user's device</li>
              <li><strong>Accessibility First:</strong> We design with users who rely on assistive technologies in mind</li>
              <li><strong>Developer-Friendly:</strong> Our tools are built to be simple, fast, and practical for everyday workflows</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>Feedback & Support</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              We're always looking to improve our tools. If you have feedback, feature requests, or encounter any issues, 
              we'd love to hear from you. You can reach us at{' '}
              <a href="mailto:echolog07@gmail.com" className={`${theme.link} underline`}>echolog07@gmail.com</a>.
            </p>
            <p className={`mb-4 ${theme.text} leading-7`}>
              For more information about EchoLog, visit our{' '}
              <a href="https://echologai.com/about/" target="_blank" rel="noopener noreferrer" className={`${theme.link} underline`}>
                main About page
              </a>.
            </p>
          </section>

          <section>
            <h2 className={`text-2xl font-semibold mb-4 ${theme.text}`}>Open Source & Contributions</h2>
            <p className={`mb-4 ${theme.text} leading-7`}>
              While JSON Formatter is currently a proprietary tool, we're committed to transparency and may consider open-sourcing 
              parts of our toolset in the future. We believe in building tools that developers can trust and rely on.
            </p>
          </section>
        </div>

        <div className={`mt-8 pt-6 border-t ${theme.border}`}>
          <a 
            href="/" 
            className={`inline-flex items-center ${theme.link} hover:underline`}
          >
            ← Back to JSON Formatter
          </a>
        </div>
      </div>
      <Footer darkMode={darkMode} />
    </div>
  );
};

export default AboutPage;

